import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { fiDoesLightWeightObjectHaveChildren } from '@fi-shared/utils/fi-does-light-weight-object-have-children';
import { isDefined } from '@fi-shared/utils';
import { MatMenuTrigger } from '@angular/material/menu';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { FiDropdownItemDisabledFn } from '@fi-shared/fi-select-search/fi-select-search';
import { FiMenuMultiSelectItemSearchHelperService } from '@fi-shared/fi-menu-multi-select-item-search/fi-menu-multi-select-item-search-helper.service';

@Component({
    selector: 'fi-menu-foldable-item',
    templateUrl: './fi-menu-foldable-item.component.html',
    styleUrls: ['./fi-menu-foldable-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-menu-foldable-item' },
    standalone: false,
})
export class FiMenuFoldableItemComponent<T extends { ChildItems: T[]; Id: number }> implements OnInit {
    @Input({ required: true }) item: T;
    @Input({ required: true }) displayValue: string;
    @Input({ required: true }) searchText: string;
    @Input({ required: true }) externalFilterFn: FiDropdownItemDisabledFn<T>;

    @Input({ required: true }) allSelectedChildItemIds: number[] = [];
    @Output() allSelectedChildItemIdsChange = new EventEmitter<number[]>();

    @Input() isMultiSelect: boolean = true;
    @Input() isParentItemDisabled: boolean;
    @Input() isParentSelected: boolean;
    @Input() preventDisableChildItem: boolean;

    @Input() itemsDisabledTooltipText: string;
    @Input() isParentInaccessible: boolean;

    @Input() isMainItemToggleHidden: boolean;
    @Input() isSubMenusLayout: boolean;
    @Input() tooltipText: string;
    @Input() subMenuHeading: string;
    @Input() isSubmenuItemSelectionRequired: boolean;
    @Input() isSelectAllFeatureEnabled: boolean;
    @Input() selectAllChildrenOnMainSelect: boolean;

    @Input() externalItemDisabledFn: FiDropdownItemDisabledFn<T>;
    @Output() onMainItemSelectionChange = new EventEmitter<void>();

    @Output() openMatMenu = new EventEmitter<MatMenuTrigger>();
    @Output() closeMatMenus = new EventEmitter<void>();
    @ViewChild(MatMenuTrigger) matMenuTrigger: MatMenuTrigger;

    isFoldedMenuExpanded: boolean = true;
    isAllSelected: boolean = false;
    isTooltipShown: boolean = false;
    private mainItemCheckbox: MatCheckbox;

    private readonly fiMenuMultiSelectItemSearchHelperService = inject(FiMenuMultiSelectItemSearchHelperService);

    ngOnInit() {
        this.setIsAllSelected();
    }

    protected getDisplayValue(item: T): string {
        return item[this.displayValue] /* + '(' + item.Id + ')'*/;
    }

    protected getFilteredChildItems(item: T): T[] {
        if (!item.ChildItems) {
            return [];
        }
        const filteredChildItems = this.fiMenuMultiSelectItemSearchHelperService.getFilteredItems({
            items: item.ChildItems,
            searchText: this.searchText,
            displayValue: this.displayValue,
            externalFilterFn: this.externalFilterFn,
        });

        return filteredChildItems;
    }

    private getCurrentSelectedIds() {
        return this.item.ChildItems.filter((childItem) => this.allSelectedChildItemIds.includes(childItem.Id)).map(
            (childItem) => childItem.Id,
        );
    }

    private mergeSelection(itemIdsToAdd: number[], itemIdsToRemove: number[]) {
        let isSelectionChanged = false;

        if (!this.isMultiSelect && itemIdsToAdd.length) {
            // here is only place where we should handle single select of child items
            this.allSelectedChildItemIds.length = 0;
            this.allSelectedChildItemIds.push(itemIdsToAdd[0]);
            isSelectionChanged = true;
        } else {
            itemIdsToAdd.forEach((id) => {
                if (!this.allSelectedChildItemIds.includes(id)) {
                    this.allSelectedChildItemIds.push(id);
                    isSelectionChanged = true;
                }
            });
        }

        itemIdsToRemove.forEach((id) => {
            const idx = this.allSelectedChildItemIds.indexOf(id);
            if (idx !== -1) {
                this.allSelectedChildItemIds.splice(idx, 1);
                isSelectionChanged = true;
            }
        });

        if (isSelectionChanged) {
            this.allSelectedChildItemIdsChange.emit(this.allSelectedChildItemIds);
        }
    }

    selectAll(isAllSelected: boolean, $event?: MouseEvent) {
        if (isDefined($event)) {
            $event.stopPropagation();
        }
        this.mainItemSelectionChange();
        this.isAllSelected = isAllSelected;
        this.isTooltipShown = !isAllSelected;

        const itemIds: number[] = this.item.ChildItems.map((childItem: T) => childItem.Id);
        if (this.isAllSelected) {
            this.mergeSelection(itemIds, []);
        } else {
            this.mergeSelection([], itemIds);
        }
    }

    isOnlySomeChildItemsSelected(): boolean {
        return this.isSomeChildItemsSelected() && !this.isAllSelected;
    }

    isMainItemIndeterminate() {
        return (
            this.selectAllChildrenOnMainSelect &&
            this.isOnlySomeChildItemsSelected() &&
            !this.isEveryChildItemsSelected()
        );
    }
    isMainItemSelected(): boolean {
        return this.selectAllChildrenOnMainSelect ? this.isEveryChildItemsSelected() : this.isSomeChildItemsSelected();
    }

    isSomeChildItemsSelected(): boolean {
        return !!this.item.ChildItems?.length
            ? this.getFilteredChildItems(this.item).some((childItem: T) => this.isChildItemSelected(childItem))
            : this.isParentSelected;
    }

    isEveryChildItemsSelected(): boolean {
        const filteredChildItems = this.getFilteredChildItems(this.item);
        return (
            !!filteredChildItems.length &&
            filteredChildItems.every((childItem: T) => this.isChildItemSelected(childItem))
        );
    }

    onChildItemClick($event: MouseEvent, childItem: T) {
        $event.stopPropagation();
        this.toggleItemSelection(childItem);
        this.setIsAllSelected();
    }

    private setIsAllSelected() {
        this.isAllSelected = this.item.ChildItems.every((childItem) => this.isChildItemSelected(childItem));
    }

    private toggleItemSelection(childItem: T) {
        if (!this.isChildItemSelected(childItem)) {
            this.mergeSelection([childItem.Id], []);
        } else {
            this.mergeSelection([], [childItem.Id]);
        }
        this.setIsAllSelected();
    }

    isChildItemSelected(childItem: T): boolean {
        return this.allSelectedChildItemIds.includes(childItem.Id);
    }

    isChildItemDisabled(childItem: T): boolean {
        const isDisabled: boolean = isDefined(this.externalItemDisabledFn)
            ? this.externalItemDisabledFn(childItem, this.item.ChildItems)
            : false;

        if (this.preventDisableChildItem) return isDisabled;

        return (
            isDisabled ||
            (this.isParentItemDisabled &&
                this.getCurrentSelectedIds().length === 1 &&
                this.isChildItemSelected(childItem))
        );
    }

    isSelectAllDisabled(): boolean {
        return this.isAllSelected || this.item.ChildItems?.every(this.isChildItemDisabled.bind(this));
    }

    onMainMenuItemClick($event: MouseEvent) {
        $event.stopPropagation();
        if (!this.isMultiSelect || !this.item.ChildItems?.length) {
            this.onMainItemSelectionChange.emit();
        }

        if (this.isSomeChildItemsSelected() && !this.isParentItemDisabled) {
            this.selectAll(false);
            this.mainItemSelectionChange();
        }
    }

    mainItemSelectionChange(mainItemCheckbox?: MatCheckbox) {
        if (this.isParentSelected || !this.isSubmenuItemSelectionRequired) {
            this.onMainItemSelectionChange.emit();
            return;
        }
        this.isTooltipShown = true;
        this.mainItemCheckbox = mainItemCheckbox;
    }

    toggleExpanded() {
        this.isFoldedMenuExpanded = !this.isFoldedMenuExpanded;
    }

    getFoldToggleIconClass(): string {
        return this.isFoldedMenuExpanded ? 'fa-light fa-square-minus' : 'fa-light fa-square-plus';
    }

    onChildMenuSelectionChange($event: MouseEvent, childItem: T) {
        $event.stopPropagation();
        if (!this.getCurrentSelectedIds().length) {
            if (!this.selectAllChildrenOnMainSelect) this.mainItemSelectionChange();
            this.mergeSelection([childItem.Id], []);
        } else {
            this.toggleItemSelection(childItem);
            if (!this.getCurrentSelectedIds().length && !this.selectAllChildrenOnMainSelect) {
                this.mainItemSelectionChange();
            }
        }
        this.setIsAllSelected();
        this.isTooltipShown = !this.getCurrentSelectedIds().length;
    }

    openMenu(trigger: MatMenuTrigger) {
        if (this.isSubMenuHidden()) return;

        this.openMatMenu.emit(trigger);
    }

    onPlainItemMouseEnter() {
        this.closeMatMenus.emit();
    }

    handleSubMenuClosed() {
        if (this.mainItemCheckbox) {
            this.mainItemCheckbox.checked = this.isMainItemSelected();
            this.mainItemCheckbox.indeterminate = this.isMainItemIndeterminate();
        }
        this.isTooltipShown = false;
    }

    closeMenu() {
        this.matMenuTrigger?.closeMenu();
    }

    isSubMenuHidden(): boolean {
        return !this.item.ChildItems?.length;
    }

    protected readonly optionDisabledFn = fiDoesLightWeightObjectHaveChildren;
    protected readonly FiInfoIconMode = FiInfoIconMode;
}
